import React, {useEffect, useRef, useState} from 'react';
import {AlertCircle, ArrowLeft, Loader2, Lock, MessageCircleMore, Send, Sparkles, User, X} from 'lucide-react';
import api from '../api/axios';
import {useAuth} from '../features/auth/AuthContext';

interface Message {
    id: number;
    role: 'USER' | 'ASSISTANT';
    content: string;
    timestamp: Date;
}

interface ChatHistoryItem {
    id: number;
    role: 'USER' | 'ASSISTANT';
    content: string;
    createdAt?: string | null;
}

interface BotCharacter {
    type: string;
    name: string;
    role: string;
    desc: string;
    color: string;
    imageUrl: string;
    locked?: boolean;
}

const FALLBACK_AVATAR =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64' viewBox='0 0 24 24' fill='none' stroke='%23cbd5e1' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2'/%3E%3Ccircle cx='12' cy='7' r='4'/%3E%3C/svg%3E";

const BOTS: BotCharacter[] = [
    {
        type: 'takahashi',
        name: '타카하시 선생님',
        role: '문법 교정',
        desc: '문장을 꼼꼼히 고쳐주고 자연스러운 예문과 이유를 차분하게 설명합니다.',
        color: 'bg-blue-500',
        imageUrl: '/images/bots/takahashi.png'
    },
    {
        type: 'hoshino',
        name: '호시노',
        role: '초보자 친구',
        desc: 'N5-N4 학습자에게 쉬운 단어와 짧은 문장으로 대화합니다.',
        color: 'bg-green-500',
        imageUrl: '/images/bots/hoshino.png'
    },
    {
        type: 'kita',
        name: '키타',
        role: '요즘 일본어',
        desc: '가벼운 반말과 자연스러운 회화체로 젊은 세대 표현을 알려줍니다.',
        color: 'bg-pink-500',
        imageUrl: '/images/bots/kita.png'
    },
    {
        type: 'yoshida',
        name: '요시다',
        role: '오사카 사투리',
        desc: '오사카 느낌의 친근한 말투로 표준어와 사투리 차이를 함께 알려줍니다.',
        color: 'bg-orange-500',
        imageUrl: '/images/bots/yoshida.png'
    },
    {
        type: 'sato',
        name: '사토',
        role: '비즈니스 일본어',
        desc: '회의, 메일, 면접에 어울리는 정중한 일본어와 격식을 다룹니다.',
        color: 'bg-slate-700',
        imageUrl: '/images/bots/sato.png'
    },
    {
        type: 'kobayashi',
        name: '코바야시',
        role: '우아한 표현',
        desc: '부드럽고 품위 있는 말투로 뉘앙스와 예쁜 표현을 알려줍니다.',
        color: 'bg-rose-400',
        imageUrl: '/images/bots/kobayashi.png'
    },
    {
        type: 'yamada',
        name: '야마다',
        role: '학생 회화',
        desc: '학교와 친구 사이에서 쓰는 자연스러운 일상 회화를 발랄하게 연습합니다.',
        color: 'bg-indigo-500',
        imageUrl: '/images/bots/yamada.png'
    },
    {
        type: 'mizuki',
        name: '미즈키',
        role: '문제 출제 코치',
        desc: 'JLPT 급수에 맞춰 문제를 내고 채점과 해설을 제공합니다.',
        color: 'bg-emerald-500',
        imageUrl: '/images/bots/mizuki.png'
    },
    {
        type: 'empty1',
        name: 'Coming Soon',
        role: '준비 중',
        desc: '새로운 봇이 곧 추가됩니다.',
        color: 'bg-gray-300',
        imageUrl: '/images/bots/locked.png',
        locked: true
    },
    {
        type: 'empty2',
        name: 'Coming Soon',
        role: '준비 중',
        desc: '새로운 봇이 곧 추가됩니다.',
        color: 'bg-gray-300',
        imageUrl: '/images/bots/locked.png',
        locked: true
    },
];

const AiChatBot = () => {
    const {isAuthenticated} = useAuth();
    const [selectedBot, setSelectedBot] = useState<BotCharacter | null>(null);
    const [showLoginPopup, setShowLoginPopup] = useState(false);
    const [input, setInput] = useState('');
    const [messages, setMessages] = useState<Message[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (selectedBot) {
            inputRef.current?.focus({ preventScroll: true });
        }
    }, [selectedBot]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);

    const getInitialMessage = (bot: BotCharacter): Message => ({
        id: 0,
        role: 'ASSISTANT',
        content: `안녕하세요. 저는 ${bot.name}입니다. ${bot.desc}\n오늘은 어떤 일본어를 연습해볼까요?`,
        timestamp: new Date(),
    });

    const handleBotSelect = async (bot: BotCharacter) => {
        if (bot.locked) return;

        if (!isAuthenticated) {
            setShowLoginPopup(true);
            return;
        }

        setSelectedBot(bot);
        setMessages([]);
        setIsLoading(true);

        try {
            const response = await api.get('/api/chat/history', {
                params: {botType: bot.type}
            });

            const historyMessages = (response.data as ChatHistoryItem[]).map((item) => ({
                id: item.id,
                role: item.role,
                content: item.content,
                timestamp: item.createdAt ? new Date(item.createdAt) : new Date(),
            }));

            setMessages(historyMessages.length > 0 ? historyMessages : [getInitialMessage(bot)]);
        } catch (error) {
            console.error('Chat history load failed:', error);
            setMessages([getInitialMessage(bot)]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSend = async (e?: React.FormEvent) => {
        e?.preventDefault();

        if (!input.trim() || isLoading || !selectedBot) return;

        const userMessage: Message = {
            id: Date.now(),
            role: 'USER',
            content: input,
            timestamp: new Date(),
        };

        setMessages((prev) => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);

        try {
            const response = await api.post('/api/chat/ask', {
                message: userMessage.content,
                botType: selectedBot.type
            });

            const aiMessage: Message = {
                id: Date.now() + 1,
                role: 'ASSISTANT',
                content: response.data?.reply || response.data?.data?.reply || '응답을 생성하지 못했습니다.',
                timestamp: new Date(),
            };
            setMessages((prev) => [...prev, aiMessage]);
        } catch (error) {
            console.error('Chat Error:', error);
            setMessages((prev) => [
                ...prev,
                {
                    id: Date.now() + 1,
                    role: 'ASSISTANT',
                    content: '서버와 연결하는 중 문제가 발생했습니다. 잠시 후 다시 시도해 주세요.',
                    timestamp: new Date(),
                }
            ]);
        } finally {
            setIsLoading(false);
        }
    };

    if (!selectedBot) {
        return (
            <div className="max-w-5xl mx-auto py-8 animate-fade-in px-4">
                <div className="text-center space-y-3 mb-10">
                    <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 flex items-center justify-center gap-2">
                        <MessageCircleMore className="w-8 h-8 text-indigo-600 dark:text-indigo-400"/>
                        AI 파트너 선택
                    </h2>
                    <p className="text-gray-500 dark:text-gray-400">원하는 성격의 AI 봇과 일본어 대화를 연습해보세요.</p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                    {BOTS.map((bot) => (
                        <button
                            key={bot.type}
                            onClick={() => handleBotSelect(bot)}
                            className={`relative flex flex-col items-center p-6 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-3xl shadow-sm transition-all duration-300 text-center ${
                                bot.locked
                                    ? 'opacity-70 cursor-not-allowed'
                                    : 'hover:shadow-xl hover:-translate-y-2 hover:border-indigo-100 dark:hover:border-indigo-900 cursor-pointer group'
                            }`}
                        >
                            <div
                                className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 shadow-sm overflow-hidden bg-white dark:bg-gray-700 border-2 border-transparent ${!bot.locked && 'group-hover:scale-110 transition-transform group-hover:border-indigo-100 dark:group-hover:border-indigo-900'}`}>
                                <img
                                    src={bot.imageUrl}
                                    alt={bot.name}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        e.currentTarget.src = FALLBACK_AVATAR;
                                    }}
                                />
                            </div>
                            <h3 className="text-base font-bold text-gray-800 dark:text-gray-100 mb-1">{bot.name}</h3>
                            <span
                                className={`text-[10px] font-bold px-2 py-0.5 rounded-full mb-2 ${bot.locked ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500' : 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'}`}>
                                {bot.role}
                            </span>
                            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed line-clamp-3">
                                {bot.desc}
                            </p>

                            {bot.locked && (
                                <div
                                    className="absolute inset-0 bg-white/40 dark:bg-black/40 rounded-3xl flex items-center justify-center backdrop-blur-[1px]">
                                    <Lock className="w-8 h-8 text-gray-400 dark:text-gray-600"/>
                                </div>
                            )}
                        </button>
                    ))}
                </div>

                {showLoginPopup && (
                    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center animate-fade-in p-4">
                        <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 max-w-sm w-full text-center shadow-2xl relative">
                            <button
                                onClick={() => setShowLoginPopup(false)}
                                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                            >
                                <X className="w-5 h-5"/>
                            </button>
                            <div
                                className="w-16 h-16 bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 rounded-full flex items-center justify-center mx-auto mb-4">
                                <AlertCircle className="w-8 h-8"/>
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">로그인이 필요합니다</h3>
                            <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
                                AI 채팅 기록을 저장하려면 먼저 로그인해 주세요.
                            </p>
                            <button
                                onClick={() => setShowLoginPopup(false)}
                                className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-colors"
                            >
                                확인
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div
            className="max-w-5xl mx-auto h-[650px] flex flex-col bg-white dark:bg-gray-800 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden mt-1 animate-fade-in-up">
            <div className={`${selectedBot.color} p-4 flex items-center gap-4 shadow-md z-10 transition-colors duration-500`}>
                <button
                    onClick={() => setSelectedBot(null)}
                    className="p-2 bg-white/20 hover:bg-white/30 rounded-full text-white transition-colors"
                >
                    <ArrowLeft className="w-5 h-5"/>
                </button>
                <div className="w-10 h-10 rounded-full overflow-hidden bg-white/20 border-2 border-white/50 shadow-sm shrink-0">
                    <img
                        src={selectedBot.imageUrl}
                        alt={selectedBot.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                            e.currentTarget.src = FALLBACK_AVATAR;
                        }}
                    />
                </div>
                <div>
                    <h2 className="text-white font-bold text-lg flex items-center gap-2">
                        {selectedBot.name}
                        <Sparkles className="w-4 h-4 text-yellow-300 animate-pulse"/>
                    </h2>
                    <p className="text-white/80 text-xs">{selectedBot.role}</p>
                </div>
            </div>

            <div 
                ref={scrollRef}
                className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-900/50 scrollbar-hide"
            >
                {messages.map((msg) => (
                    <div
                        key={msg.id}
                        className={`flex items-start gap-3 ${
                            msg.role === 'USER' ? 'flex-row-reverse' : 'flex-row'
                        }`}
                    >
                        <div
                            className={`w-9 h-9 rounded-2xl flex items-center justify-center shrink-0 shadow-sm overflow-hidden ${
                                msg.role === 'USER' ? 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-300' : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
                            }`}
                        >
                            {msg.role === 'USER' ? (
                                <User className="w-5 h-5"/>
                            ) : (
                                <img
                                    src={selectedBot.imageUrl}
                                    alt={selectedBot.name}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        e.currentTarget.src = FALLBACK_AVATAR;
                                    }}
                                />
                            )}
                        </div>

                        <div
                            className={`max-w-[80%] p-3.5 rounded-2xl text-[15px] leading-relaxed shadow-sm whitespace-pre-wrap ${
                                msg.role === 'USER'
                                    ? 'bg-indigo-600 text-white rounded-tr-none'
                                    : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border border-gray-100 dark:border-gray-700 rounded-tl-none'
                            }`}
                        >
                            {msg.content}
                        </div>
                    </div>
                ))}

                {isLoading && (
                    <div className="flex items-start gap-3 animate-pulse">
                        <div
                            className="w-9 h-9 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center shadow-sm overflow-hidden">
                            <img
                                src={selectedBot.imageUrl}
                                alt={selectedBot.name}
                                className="w-full h-full object-cover opacity-50"
                                onError={(e) => {
                                    e.currentTarget.src = FALLBACK_AVATAR;
                                }}
                            />
                        </div>
                        <div
                            className="bg-white dark:bg-gray-800 p-3 rounded-2xl rounded-tl-none border border-gray-100 dark:border-gray-700 shadow-sm flex items-center gap-2">
                            <Loader2 className="w-4 h-4 text-indigo-500 animate-spin"/>
                            <span className="text-xs text-gray-400 dark:text-gray-500">답변을 생각하는 중...</span>
                        </div>
                    </div>
                )}
            </div>

            <form onSubmit={handleSend} className="p-4 bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700 flex gap-2">
                <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={`${selectedBot.name}에게 메시지 보내기...`}
                    className="flex-1 px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 dark:focus:ring-indigo-900 transition-all text-sm dark:text-gray-100"
                    disabled={isLoading}
                />
                <button
                    type="submit"
                    disabled={!input.trim() || isLoading}
                    className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed text-white px-5 py-3 rounded-xl transition-colors shadow-sm flex items-center justify-center"
                >
                    {isLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin"/>
                    ) : (
                        <Send className="w-5 h-5"/>
                    )}
                </button>
            </form>
        </div>
    );
};

export default AiChatBot;
